import { useState } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Transaction } from "@solana/web3.js";

interface DepositModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function DepositModal({ open, onOpenChange, onSuccess }: DepositModalProps) {
  const [amount, setAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingDepositId, setPendingDepositId] = useState<number | null>(null);
  const { connection } = useConnection();
  const { publicKey, signTransaction } = useWallet();
  const { toast } = useToast();

  const handleDeposit = async () => {
    if (!publicKey || !signTransaction) {
      toast({
        title: "Error",
        description: "Please connect your wallet first",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);

      const tokenAmount = parseFloat(amount);
      if (isNaN(tokenAmount) || tokenAmount <= 0) {
        throw new Error("Invalid amount");
      }

      // Get transaction from server
      const prepareResponse = await fetch("/api/user/prepare-deposit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: tokenAmount,
          walletAddress: publicKey.toString(),
        }),
      });

      if (!prepareResponse.ok) {
        const error = await prepareResponse.text();
        throw new Error(error);
      }

      const { pendingDepositId: newPendingDepositId, transaction: serializedTransaction, blockhash, lastValidBlockHeight } = await prepareResponse.json();

      // Store the pending deposit ID
      setPendingDepositId(newPendingDepositId);

      // Deserialize and sign transaction
      const transaction = Transaction.from(Buffer.from(serializedTransaction, 'base64'));
      const signedTransaction = await signTransaction(transaction);

      // Send signed transaction
      const rawTransaction = signedTransaction.serialize();
      const txSignature = await connection.sendRawTransaction(rawTransaction);

      // Wait for confirmation
      const confirmation = await connection.confirmTransaction({
        signature: txSignature,
        blockhash,
        lastValidBlockHeight,
      });

      if (confirmation.value.err) {
        throw new Error("Transaction failed to confirm");
      }

      // Notify backend about successful deposit
      const response = await fetch("/api/user/deposit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: tokenAmount,
          transaction: txSignature,
          pendingDepositId: newPendingDepositId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to process deposit");
      }

      toast({
        title: "Deposit successful",
        description: `${amount} Cacatal Brasileiro tokens deposited successfully`,
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Deposit error:", error);
      toast({
        title: "Deposit failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setPendingDepositId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deposit Cacatal Brasileiro</DialogTitle>
          <DialogDescription>
            Enter the amount of Cacatal Brasileiro tokens you want to deposit.
            The tokens will be transferred to the treasury wallet.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Input
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <Button
            onClick={handleDeposit}
            disabled={!amount || isProcessing || !publicKey}
            className="w-full"
          >
            {isProcessing ? "Processing..." : "Deposit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}